import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  CreditCard, 
  Building2, 
  Plus, 
  Search, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Trash2,
  Eye,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentManagement({ students }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  
  // Yeni ödeme formu
  const [newPayment, setNewPayment] = useState({
    user_id: '',
    amount: '',
    payment_type: 'education_fee',
    payment_method: 'credit_card',
    installment_count: 1,
    description: ''
  });

  useEffect(() => {
    loadPayments();
  }, [filterStatus, filterType]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/payments`;
      const params = [];
      if (filterStatus) params.push(`status=${filterStatus}`);
      if (filterType) params.push(`payment_type=${filterType}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url, { withCredentials: true });
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Ödemeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!newPayment.user_id || !newPayment.amount) {
      toast.error('Öğrenci ve tutar gerekli');
      return;
    }

    try {
      await axios.post(`${API}/admin/payments`, {
        ...newPayment,
        amount: parseFloat(newPayment.amount),
        installment_count: parseInt(newPayment.installment_count)
      }, { withCredentials: true });
      
      toast.success('Ödeme tanımlandı');
      setShowAddModal(false);
      setNewPayment({
        user_id: '',
        amount: '',
        payment_type: 'education_fee',
        payment_method: 'credit_card',
        installment_count: 1,
        description: ''
      });
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ödeme eklenemedi');
    }
  };

  const handlePayInstallment = async (paymentId, installmentNo) => {
    try {
      await axios.post(`${API}/admin/payments/${paymentId}/installment/${installmentNo}`, {}, { withCredentials: true });
      toast.success('Taksit ödendi');
      loadPayments();
      // Detail modal'ı güncelle
      if (selectedPayment?.id === paymentId) {
        const updated = payments.find(p => p.id === paymentId);
        if (updated) setSelectedPayment(updated);
      }
    } catch (error) {
      toast.error('Taksit ödenemedi');
    }
  };

  const handleMarkAsPaid = async (paymentId) => {
    try {
      await axios.put(`${API}/admin/payments/${paymentId}`, {
        status: 'completed'
      }, { withCredentials: true });
      toast.success('Ödeme tamamlandı');
      loadPayments();
    } catch (error) {
      toast.error('Güncelleme hatası');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/admin/payments/${paymentId}`, { withCredentials: true });
      toast.success('Ödeme silindi');
      loadPayments();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Silme hatası');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Ödendi</span>;
      case 'partial':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Kısmi</span>;
      default:
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Bekliyor</span>;
    }
  };

  const getPaymentTypeName = (type) => {
    switch (type) {
      case 'education_fee': return 'Eğitim Ücreti';
      case 'exam_fee': return 'Sınav Harcı';
      case 'certificate_fee': return 'Sertifika Ücreti';
      default: return type;
    }
  };

  const filteredPayments = payments.filter(p => 
    !searchQuery || 
    p.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Özet istatistikler
  const summary = {
    total: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
    paid: payments.reduce((sum, p) => sum + (p.paid_amount || 0), 0),
    pending: payments.filter(p => p.status === 'pending').length,
    completed: payments.filter(p => p.status === 'completed').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Ödeme Yönetimi</h2>
          <p className="text-gray-500 text-sm mt-1">Öğrenci ödemelerini yönetin ve takip edin</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" />
          Yeni Ödeme Tanımla
        </Button>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Toplam Tutar</p>
            <p className="text-2xl font-bold">{summary.total.toLocaleString('tr-TR')} ₺</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-green-100 text-sm">Tahsil Edilen</p>
            <p className="text-2xl font-bold">{summary.paid.toLocaleString('tr-TR')} ₺</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-orange-100 text-sm">Bekleyen</p>
            <p className="text-2xl font-bold">{summary.pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-emerald-100 text-sm">Tamamlanan</p>
            <p className="text-2xl font-bold">{summary.completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtreler */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Öğrenci ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-10 px-3 border rounded-lg bg-white text-sm"
        >
          <option value="">Tüm Durumlar</option>
          <option value="pending">Bekleyen</option>
          <option value="partial">Kısmi Ödeme</option>
          <option value="completed">Tamamlanan</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="h-10 px-3 border rounded-lg bg-white text-sm"
        >
          <option value="">Tüm Türler</option>
          <option value="education_fee">Eğitim Ücreti</option>
          <option value="exam_fee">Sınav Harcı</option>
          <option value="certificate_fee">Sertifika Ücreti</option>
        </select>
      </div>

      {/* Ödeme Listesi */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Henüz ödeme kaydı yok</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Öğrenci</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Tür</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Tutar</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Ödenen</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Taksit</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Durum</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-gray-900">{payment.user_name}</p>
                          <p className="text-sm text-gray-500">{payment.user_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">{getPaymentTypeName(payment.payment_type)}</span>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-gray-900">{payment.amount?.toLocaleString('tr-TR')} ₺</span>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-green-600">{(payment.paid_amount || 0).toLocaleString('tr-TR')} ₺</span>
                      </td>
                      <td className="p-4">
                        {payment.installment_count > 1 ? (
                          <span className="text-sm text-blue-600">{payment.installment_count} Taksit</span>
                        ) : (
                          <span className="text-sm text-gray-500">Tek Çekim</span>
                        )}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowDetailModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {payment.status !== 'completed' && payment.installment_count === 1 && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-green-600"
                              onClick={() => handleMarkAsPaid(payment.id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeletePayment(payment.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Yeni Ödeme Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Yeni Ödeme Tanımla</CardTitle>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Öğrenci Seçimi - Autocomplete */}
              <div className="relative">
                <Label>Öğrenci Ara ve Seç</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="İsim veya e-posta yazın..."
                    value={studentSearchQuery}
                    onChange={(e) => {
                      setStudentSearchQuery(e.target.value);
                      setShowStudentDropdown(true);
                    }}
                    onFocus={() => setShowStudentDropdown(true)}
                    className="pl-9"
                  />
                </div>
                
                {/* Seçili Öğrenci */}
                {newPayment.user_id && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-blue-700">
                      {students?.find(s => s.id === newPayment.user_id)?.name}
                    </span>
                    <button 
                      onClick={() => setNewPayment({ ...newPayment, user_id: '' })}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {/* Autocomplete Dropdown */}
                {showStudentDropdown && studentSearchQuery.length >= 2 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                    {students?.filter(s => 
                      s.role !== 'admin' && (
                        s.name?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                        s.email?.toLowerCase().includes(studentSearchQuery.toLowerCase())
                      )
                    ).slice(0, 6).map(student => (
                      <div 
                        key={student.id}
                        className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                        onClick={() => {
                          setNewPayment({ ...newPayment, user_id: student.id });
                          setStudentSearchQuery('');
                          setShowStudentDropdown(false);
                        }}
                      >
                        <p className="font-medium text-sm">{student.name}</p>
                        <p className="text-xs text-gray-500">{student.email}</p>
                      </div>
                    ))}
                    {students?.filter(s => 
                      s.role !== 'admin' && (
                        s.name?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                        s.email?.toLowerCase().includes(studentSearchQuery.toLowerCase())
                      )
                    ).length === 0 && (
                      <p className="p-3 text-center text-gray-500 text-sm">Öğrenci bulunamadı</p>
                    )}
                  </div>
                )}
              </div>

              {/* Ödeme Türü */}
              <div>
                <Label>Ödeme Türü</Label>
                <select
                  value={newPayment.payment_type}
                  onChange={(e) => setNewPayment({ ...newPayment, payment_type: e.target.value })}
                  className="w-full h-10 px-3 border rounded-lg bg-white text-sm mt-1"
                >
                  <option value="education_fee">Eğitim Ücreti</option>
                  <option value="exam_fee">Sınav Harcı</option>
                  <option value="certificate_fee">Sertifika Ücreti</option>
                </select>
              </div>

              {/* Tutar */}
              <div>
                <Label>Tutar (₺)</Label>
                <Input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>

              {/* Ödeme Yöntemi */}
              <div>
                <Label>Ödeme Yöntemi</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setNewPayment({ ...newPayment, payment_method: 'credit_card' })}
                    className={`flex-1 p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-colors ${
                      newPayment.payment_method === 'credit_card' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className="w-5 h-5" />
                    Kredi Kartı
                  </button>
                  <button
                    onClick={() => setNewPayment({ ...newPayment, payment_method: 'bank_transfer' })}
                    className={`flex-1 p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-colors ${
                      newPayment.payment_method === 'bank_transfer' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Building2 className="w-5 h-5" />
                    Havale/EFT
                  </button>
                </div>
              </div>

              {/* Taksit */}
              <div>
                <Label>Taksit Seçeneği</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {[1, 2, 3, 4, 6, 9].map(num => (
                    <button
                      key={num}
                      onClick={() => setNewPayment({ ...newPayment, installment_count: num })}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                        newPayment.installment_count === num 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {num === 1 ? 'Tek Çekim' : `${num} Taksit`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Açıklama */}
              <div>
                <Label>Açıklama (Opsiyonel)</Label>
                <Input
                  value={newPayment.description}
                  onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
                  placeholder="2024 Eğitim Dönemi"
                  className="mt-1"
                />
              </div>

              {/* Taksit Özeti */}
              {newPayment.amount && newPayment.installment_count > 1 && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">{newPayment.installment_count} taksit x </span>
                    <span className="font-bold">{(parseFloat(newPayment.amount) / newPayment.installment_count).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                  İptal
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleCreatePayment}>
                  Ödeme Tanımla
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ödeme Detay Modal */}
      {showDetailModal && selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10">
              <CardTitle>Ödeme Detayı</CardTitle>
              <button onClick={() => setShowDetailModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Öğrenci Bilgisi */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">{selectedPayment.user_name}</p>
                <p className="text-sm text-gray-500">{selectedPayment.user_email}</p>
              </div>

              {/* Ödeme Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Tür</p>
                  <p className="font-medium">{getPaymentTypeName(selectedPayment.payment_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Durum</p>
                  {getStatusBadge(selectedPayment.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Toplam Tutar</p>
                  <p className="font-bold text-lg">{selectedPayment.amount?.toLocaleString('tr-TR')} ₺</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ödenen</p>
                  <p className="font-bold text-lg text-green-600">{(selectedPayment.paid_amount || 0).toLocaleString('tr-TR')} ₺</p>
                </div>
              </div>

              {/* Taksitler */}
              {selectedPayment.installments?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Taksitler</h4>
                  <div className="space-y-2">
                    {selectedPayment.installments.map((inst) => (
                      <div 
                        key={inst.installment_no}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          inst.status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            inst.status === 'paid' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {inst.installment_no}
                          </div>
                          <div>
                            <p className="font-medium">{inst.amount?.toLocaleString('tr-TR')} ₺</p>
                            {inst.paid_date && (
                              <p className="text-xs text-gray-500">
                                Ödeme: {new Date(inst.paid_date).toLocaleDateString('tr-TR')}
                              </p>
                            )}
                          </div>
                        </div>
                        {inst.status === 'paid' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Button 
                            size="sm" 
                            onClick={() => handlePayInstallment(selectedPayment.id, inst.installment_no)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Ödendi
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tek Çekim için Ödendi Butonu */}
              {selectedPayment.installment_count === 1 && selectedPayment.status !== 'completed' && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    handleMarkAsPaid(selectedPayment.id);
                    setShowDetailModal(false);
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Ödeme Tamamlandı Olarak İşaretle
                </Button>
              )}

              <p className="text-xs text-gray-400 text-center">
                Oluşturulma: {new Date(selectedPayment.created_at).toLocaleDateString('tr-TR')}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
